var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo, useState, useCallback } from "react";
import { MaterialReactTable, useMaterialReactTable, MRT_EditActionButtons, } from "material-react-table";
import { Box, Button, IconButton, Tooltip, DialogContent, DialogActions, TextField, MenuItem, Typography, } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { CloudUpload } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { MRT_Localization_PL } from "material-react-table/locales/pl";
import validator from "validator";
import { getFileExtension, getLabelForValue, nbsp } from "../../utilities";
import { useDeleteSharableFile, useGetSharableFiles, useCreateSharableFile, useUpdateSharableFile, } from "./queries";
import { LimitedCell } from "./Cells";
import { getCommaSeparatedValues } from "../../utilities";
const MAX_NUMBER_OF_SHARED_TO = 10;
const Table = ({ types, extensions, scope, selectedType, selectedExtension, }) => {
    const [globalFilter, setGlobalFilter] = useState("");
    const [formValues, setFormValues] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const columns = useMemo(() => {
        let columns = [
            {
                accessorKey: "display_name",
                header: "Nazwa",
                Cell: ({ cell }) => (React.createElement(LimitedCell, { cell: cell, maxCharShown: 50, isLink: true })),
            },
            {
                accessorKey: "id",
                header: "id",
            },
            {
                accessorKey: "added",
                header: "Dodano",
                enableEditing: false,
            },
            {
                accessorKey: "shared_to_original",
                header: "Udostępnione dla",
                Cell: ({ cell }) => (React.createElement(Tooltip, { title: `${cell.row.original.shared_to_original}`, slotProps: {
                        tooltip: {
                            className: "!text-sm !max-w-md",
                        },
                    } },
                    React.createElement("span", { className: "text-decoration-underline cursor-auto" }, `${cell.row.original.shared_to_original.length} ${cell.row.original.shared_to_original.length === 1
                        ? "użytkownik"
                        : "użytkowników"}`))),
                muiEditTextFieldProps: {
                    required: true,
                    error: !!(validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors.shared_to_original),
                    helperText: validationErrors === null || validationErrors === void 0 ? void 0 : validationErrors.shared_to_original,
                },
            },
            {
                accessorKey: "owner__username",
                header: "Udostępnione przez",
                enableEditing: false,
                Cell: ({ cell }) => React.createElement(LimitedCell, { cell: cell, maxCharShown: 100 }),
            },
            {
                accessorKey: "file_type",
                Cell: ({ row }) => getLabelForValue(types, row.original.file_type),
                header: "Typ danych",
                editVariant: "select",
                editSelectOptions: types,
                muiEditTextFieldProps: {
                    select: true,
                },
            },
            {
                accessorKey: "file_extension",
                header: "Rozszerzenie",
                editVariant: "select",
                editSelectOptions: extensions,
                muiEditTextFieldProps: {
                    select: true,
                },
            },
            {
                accessorKey: "description",
                header: "Opis",
                Cell: ({ cell }) => React.createElement(LimitedCell, { cell: cell, maxCharShown: 100 }),
            },
        ];
        if (scope !== "shared_to_me") {
            columns = columns.filter((c) => c.accessorKey !== "owner__username");
        }
        if (scope !== "shared_to_subset") {
            columns = columns.filter((c) => c.accessorKey !== "shared_to_original");
        }
        return columns;
    }, [scope, validationErrors]);
    const { mutateAsync: createSharableFile, isPending: isCreatingSharableFile } = useCreateSharableFile(formValues, scope);
    const { data: fetchedFiles = [], isError: isLoadingFilesError, isFetching: isFetchingFiles, isLoading: isLoadingFiles, } = useGetSharableFiles(scope);
    const { mutateAsync: deleteSharableFile, isPending: isDeletingSharableFile } = useDeleteSharableFile();
    const { mutateAsync: updateSharableFile, isPending: isUpdatingSharableFile } = useUpdateSharableFile(Object.assign({}, formValues), scope);
    const openDeleteConfirmModal = (row) => {
        if (window.confirm("Czy jesteś pewien, że chcesz usunąć te dane?")) {
            deleteSharableFile(row.original.id);
        }
    };
    const handleCreateSharableFile = useCallback((_a) => __awaiter(void 0, [_a], void 0, function* ({ values, table }) {
        var _b, _c, _d, _e, _f;
        const newValidationErrors = {
            display_name: validateDisplayName((_b = formValues.display_name) !== null && _b !== void 0 ? _b : ""),
            description: validateDescription((_c = formValues.description) !== null && _c !== void 0 ? _c : ""),
            file: validateFileExtension((_e = (_d = formValues.file) === null || _d === void 0 ? void 0 : _d.name) !== null && _e !== void 0 ? _e : "", extensions.map(({ label }) => label)),
        };
        if (scope === "shared_to_subset") {
            newValidationErrors["shared_to_original"] = validateSharedToOriginal((_f = formValues.shared_to_original) !== null && _f !== void 0 ? _f : "");
        }
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        yield createSharableFile(values);
        table.setCreatingRow(null); // close modal
        setValidationErrors({});
        setFormValues({});
    }), [scope, formValues, extensions]);
    const handleUpdateSharableFile = useCallback((_g) => __awaiter(void 0, [_g], void 0, function* ({ values, row, table }) {
        var _h, _j;
        const newValidationErrors = {};
        // conditionally only validate fields that changed
        if (formValues.display_name) {
            newValidationErrors.display_name = validateDisplayName(formValues.display_name);
        }
        if (formValues.description) {
            newValidationErrors.description = validateDescription(formValues.description);
        }
        if (formValues.file) {
            newValidationErrors.file = validateFileExtension((_j = (_h = formValues.file) === null || _h === void 0 ? void 0 : _h.name) !== null && _j !== void 0 ? _j : "", extensions.map(({ label }) => label));
        }
        if (scope === "shared_to_subset" &&
            formValues.shared_to_original !== undefined) {
            newValidationErrors.shared_to_original = validateSharedToOriginal(formValues.shared_to_original);
        }
        if (Object.values(newValidationErrors).some((error) => error)) {
            setValidationErrors(newValidationErrors);
            return;
        }
        // id is not visible in values if hidden in table
        yield updateSharableFile(Object.assign(Object.assign({}, values), { id: row.original.id }));
        table.setEditingRow(null); // close modal
        setValidationErrors({});
        setFormValues({});
    }), [scope, formValues, extensions]);
    const filteredData = useMemo(() => {
        return filterData(fetchedFiles, selectedType, selectedExtension, globalFilter);
    }, [fetchedFiles, selectedType, selectedExtension, globalFilter]);
    const table = useMaterialReactTable({
        columns,
        data: filteredData,
        initialState: {
            columnPinning: { left: ["mrt-row-actions"] },
            columnVisibility: { id: false },
            columnOrder: [
                "display_name",
                "id",
                "shared_to_original",
                "owner__username",
                "added",
                "file_type",
                "file_extension",
                "description",
            ],
            showGlobalFilter: true,
            pagination: {
                pageSize: 20,
                pageIndex: 0,
            },
        },
        onGlobalFilterChange: setGlobalFilter,
        state: {
            globalFilter,
            isLoading: isLoadingFiles,
            isSaving: isCreatingSharableFile ||
                isUpdatingSharableFile ||
                isDeletingSharableFile,
            showAlertBanner: isLoadingFilesError,
            showProgressBars: isFetchingFiles,
        },
        localization: MRT_Localization_PL,
        enableFilters: false,
        enableGlobalFilter: true,
        enableColumnPinning: true,
        enableRowActions: scope !== "shared_to_me",
        onCreatingRowSave: handleCreateSharableFile,
        onCreatingRowCancel: () => {
            setValidationErrors({});
            setFormValues({});
        },
        onEditingRowSave: handleUpdateSharableFile,
        onEditingRowCancel: () => {
            setValidationErrors({});
            setFormValues({});
        },
        muiTopToolbarProps: {
            className: scope == "shared_to_me" ? "table-toolbar--shared-to-me" : "",
        },
        muiToolbarAlertBannerProps: {
            color: "error",
            children: "Błąd przy wczytywaniu danych. Spróbuj ponownie później.",
        },
        muiPaginationProps: {
            rowsPerPageOptions: [10, 20, 50],
        },
        renderTopToolbarCustomActions: ({ table }) => scope !== "shared_to_me" && (React.createElement(Button, { variant: "contained", onClick: () => {
                table.setCreatingRow(true);
            } }, "Dodaj nowe dane")),
        renderRowActions: ({ row, table }) => (React.createElement(Box, { sx: { display: "flex", gap: "1rem" } },
            React.createElement(Tooltip, { title: "Edytuj" },
                React.createElement(IconButton, { onClick: () => table.setEditingRow(row) },
                    React.createElement(EditIcon, null))),
            React.createElement(Tooltip, { title: "Usu\u0144" },
                React.createElement(IconButton, { color: "error", onClick: () => openDeleteConfirmModal(row) },
                    React.createElement(DeleteIcon, null))))),
        renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => {
            return (scope !== "shared_to_me" && (React.createElement(React.Fragment, null,
                React.createElement("h3", { className: "fs-4 fw-bold m-4 mb-2" }, getNewDataModalTitle(scope)),
                React.createElement(DialogContent, { sx: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                        paddingTop: "1rem",
                    } },
                    React.createElement(TextField, { label: "Nazwa (3 - 100 znak\u00F3w)", sx: { marginTop: "1rem" }, onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { display_name: e.target.value }))), slotProps: { htmlInput: { maxLength: 100 } }, error: !!validationErrors.display_name, helperText: validationErrors.display_name, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { display_name: "" }))) }),
                    React.createElement(TextField, { label: "Typ danych", select: true, onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { file_type: e.target.value }))) }, types.map((type) => (React.createElement(MenuItem, { value: type.value, key: type.value }, type.label)))),
                    React.createElement(TextField, { label: "Opis (10 - 500 znak\u00F3w)", onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { description: e.target.value }))), slotProps: { htmlInput: { maxLength: 500 } }, error: !!validationErrors.description, helperText: validationErrors.description, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { description: "" }))) }),
                    scope === "shared_to_subset" && (React.createElement(React.Fragment, null,
                        React.createElement("span", { className: "fs-7" }, nbsp(`Wpisz adresy email rozdzielone przecinkiem. Jeśli użytkownik
                    posiada konto, dane zostaną mu udostępnione.`)),
                        React.createElement(Box, { sx: {
                                maxHeight: 200,
                                overflowY: "auto",
                                paddingTop: "8px",
                            } },
                            React.createElement(TextField, { label: `Udostępnianie (max ${MAX_NUMBER_OF_SHARED_TO} adresów)`, multiline: true, fullWidth: true, variant: "outlined", placeholder: "Wpisz adresy email u\u017Cytkownik\u00F3w", onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { shared_to_original: e.target.value }))), error: !!validationErrors.shared_to_original, helperText: validationErrors.shared_to_original, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { shared_to_original: "" }))) })))),
                    React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 2 } },
                        React.createElement(Button, { component: "label", role: undefined, variant: "contained", tabIndex: -1, startIcon: React.createElement(CloudUpload, null), sx: { alignSelf: "flex-start" } },
                            "Wybierz plik",
                            React.createElement("input", { type: "file", hidden: true, onChange: (e) => setFormValues((p) => {
                                    var _a;
                                    return (Object.assign(Object.assign({}, p), { file: (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0] }));
                                }), accept: extensions
                                    .map(({ label }) => `.${label}`)
                                    .join(", ") })),
                        formValues.file && (React.createElement(Typography, { variant: "body1" }, formValues.file.name))),
                    validationErrors.file && (React.createElement(Typography, { variant: "body2", color: "error" }, validationErrors.file))),
                React.createElement(DialogActions, null,
                    React.createElement(MRT_EditActionButtons, { variant: "text", table: table, row: row })))));
        },
        renderEditRowDialogContent: ({ table, row, internalEditComponents }) => {
            return (React.createElement(React.Fragment, null,
                React.createElement("h3", { className: "fs-4 fw-bold m-4 mb-2" }, "Edytuj dane"),
                React.createElement(DialogContent, { sx: {
                        display: "flex",
                        flexDirection: "column",
                        gap: "1.5rem",
                        paddingTop: "1rem",
                    } },
                    React.createElement(TextField, { label: "Nazwa (3 - 100 znak\u00F3w)", sx: { marginTop: "1rem" }, onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { display_name: e.target.value }))), defaultValue: row.original.display_name, slotProps: { htmlInput: { maxLength: 100 } }, error: !!validationErrors.display_name, helperText: validationErrors.display_name, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { display_name: "" }))) }),
                    React.createElement(TextField, { label: "Typ danych", select: true, onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { file_type: e.target.value }))), defaultValue: row.original.file_type }, types.map((type) => (React.createElement(MenuItem, { value: type.value, key: type.value }, type.label)))),
                    React.createElement(TextField, { label: "Opis (10 - 500 znak\u00F3w)", onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { description: e.target.value }))), defaultValue: row.original.description, slotProps: { htmlInput: { maxLength: 500 } }, error: !!validationErrors.description, helperText: validationErrors.description, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { description: "" }))) }),
                    scope === "shared_to_subset" && (React.createElement(React.Fragment, null,
                        React.createElement("span", { className: "fs-7" }, "Wpisz adresy email rozdzielone przecinkiem. Je\u015Bli u\u017Cytkownik posiada konto, dane zostan\u0105 mu udost\u0119pnione."),
                        React.createElement(Box, { sx: {
                                maxHeight: 200,
                                overflowY: "auto",
                                paddingTop: "8px",
                            } },
                            React.createElement(TextField, { label: `Udostępnianie (max ${MAX_NUMBER_OF_SHARED_TO} adresów)`, multiline: true, fullWidth: true, variant: "outlined", placeholder: "Wpisz adresy email u\u017Cytkownik\u00F3w", onChange: (e) => setFormValues((p) => (Object.assign(Object.assign({}, p), { shared_to_original: e.target.value }))), defaultValue: row.original.shared_to_original, error: !!validationErrors.shared_to_original, helperText: validationErrors.shared_to_original, onFocus: () => setValidationErrors((p) => (Object.assign(Object.assign({}, p), { shared_to_original: "" }))) })))),
                    React.createElement(Box, null,
                        React.createElement(Typography, { variant: "body1", sx: { marginBottom: 1 } },
                            "Obecny plik: ",
                            row.original.file_url.split("/").slice(-1)),
                        React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 2 } },
                            React.createElement(Button, { component: "label", role: undefined, variant: "contained", tabIndex: -1, startIcon: React.createElement(CloudUpload, null), sx: { alignSelf: "flex-start" } },
                                "Zmie\u0144 plik",
                                React.createElement("input", { type: "file", hidden: true, onChange: (e) => setFormValues((p) => {
                                        var _a;
                                        return (Object.assign(Object.assign({}, p), { file: (_a = e.target.files) === null || _a === void 0 ? void 0 : _a[0] }));
                                    }), accept: extensions
                                        .map(({ label }) => `.${label}`)
                                        .join(", ") })),
                            formValues.file && (React.createElement(Typography, { variant: "body1" }, formValues.file.name))),
                        validationErrors.file && (React.createElement(Typography, { variant: "body2", color: "error" }, validationErrors.file)))),
                React.createElement(DialogActions, null,
                    React.createElement(MRT_EditActionButtons, { variant: "text", table: table, row: row }))));
        },
    });
    return (React.createElement("div", { className: "table-wrapper" },
        React.createElement(MaterialReactTable, { table: table })));
};
function getNewDataModalTitle(scope) {
    return {
        private: "Dodaj prywatne dane",
        shared_to_everyone: "Dodaj publicznie dostępne dane",
        shared_to_subset: "Dodaj dane dostępne dla wybranych",
    }[scope];
}
function filterData(data, dataType, dataFileExtension, globalFilter) {
    let tableDataFiltered = data;
    if (dataType) {
        tableDataFiltered = tableDataFiltered.filter((file) => file.file_type == dataType);
    }
    if (dataFileExtension) {
        tableDataFiltered = tableDataFiltered.filter((file) => file.file_extension == dataFileExtension);
    }
    if (globalFilter) {
        tableDataFiltered = tableDataFiltered.filter((file) => file.description.toLowerCase().includes(globalFilter.toLowerCase()) ||
            file.display_name.toLowerCase().includes(globalFilter.toLowerCase()));
    }
    return tableDataFiltered;
}
const validateDescription = (description) => {
    return description.length < 10
        ? "Opis musi składać się z conajmniej 10 znaków"
        : "";
};
const validateSharedToOriginal = (shared_to_original) => {
    const wrongEmails = getWrongEmails(shared_to_original);
    const emailCandidates = getCommaSeparatedValues(shared_to_original);
    return wrongEmails.length > 0
        ? `Niepoprawny format jednego z adresów email: ${wrongEmails[0]}`
        : emailCandidates.length > MAX_NUMBER_OF_SHARED_TO
            ? `Wprowadź maksymalnie ${MAX_NUMBER_OF_SHARED_TO} adresów`
            : emailCandidates.length === 0
                ? `Wprowadź co najmniej 1 adres`
                : "";
};
const validateDisplayName = (display_name) => {
    return display_name.length < 3
        ? "Nazwa musi się składać z co najmniej 3 znaków"
        : "";
};
const validateFileExtension = (filename, allowedExtensions) => {
    const extension = getFileExtension(filename);
    return !extension || !allowedExtensions.includes(extension)
        ? `Prześlij plik w jednym z nastepujących formatów: ${allowedExtensions.join(", ")}`
        : "";
};
const getWrongEmails = (sharedToFieldValue) => {
    const emails = getCommaSeparatedValues(sharedToFieldValue);
    const wrongEmails = emails.filter((e) => !validator.isEmail(e));
    return wrongEmails;
};
export default Table;
